/**
 * What a perk on an *item* actually does — from the engine, not folklore.
 *
 * The `perk` field means two unrelated things depending on the slot, and the
 * Mod Maker's dropdown must not pretend otherwise:
 *
 * **On a weapon**, the engine never grants the perk to the wielder. Combat
 * code asks `weaponGetPerk(weapon)` and compares against the PERK_WEAPON_*
 * family, each with a hard-coded effect (`combat.cc`, `item.cc`,
 * `actions.cc`). Any other perk id in the field does nothing (the AI merely
 * *prefers* weapons whose field is set, combat_ai.cc:1985).
 *
 * **On armor**, equipping calls `perkAddEffect` (inventory.cc:3165 — party
 * members, the player included), which applies exactly two things:
 * the row's `stat += statModifier`, and — for rows with `maxRank == -1`, the
 * not-player-selectable class — the seven SPECIAL columns *as bonuses*
 * (perk.cc:563). It does NOT raise the perk's rank, so perks whose effect is
 * a `perkGetRank` check elsewhere in the engine (Awareness, Night Vision, …)
 * do nothing from armor. The dropdown therefore only offers perks whose
 * while-worn effect is real, with the bonus spelled out.
 *
 * Ids are `engine/src/perk_defs.h` enum positions; the stat/SPECIAL numbers
 * come from the generated `saveedit/perktable.ts`, never typed here.
 */

import { PERK_ROWS } from '../saveedit/perktable';

/** Display names for `stat_defs.h` Stat, index-aligned. */
const STAT_LABELS: readonly string[] = [
  'Strength', 'Perception', 'Endurance', 'Charisma', 'Intelligence', 'Agility', 'Luck',
  'max HP', 'max AP', 'Armor Class', 'unarmed damage', 'melee damage', 'carry weight (lbs)',
  'Sequence', 'healing rate', 'critical chance %', 'better criticals',
  'damage threshold', 'laser DT', 'fire DT', 'plasma DT', 'electrical DT', 'EMP DT', 'explosion DT',
  'damage resistance %', 'laser DR %', 'fire DR %', 'plasma DR %', 'electrical DR %', 'EMP DR %',
  'explosion DR %', 'radiation resistance %', 'poison resistance %', 'age', 'gender',
  'current HP', 'poison level', 'radiation level',
];

/**
 * The weapon-perk family: id -> what the combat engine does with it. The
 * `tldr` is the player-facing sentence; `source` is the establishing code.
 */
export const WEAPON_PERK_MECHANICS: ReadonlyMap<number, { tldr: string; source: string }> = new Map([
  [58, { tldr: 'Long Range — distance penalties shrink: Perception counts ×4 instead of ×2.', source: 'combat.cc:4525' }],
  [59, { tldr: 'Accurate — a flat +20% chance to hit.', source: 'combat.cc:4608' }],
  [60, { tldr: 'Penetrate — the target’s damage threshold drops to 20%: armor plating barely counts.', source: 'combat.cc:4725' }],
  [61, { tldr: 'Knockback — knocks targets back twice as far (1 hex per 5 damage, not per 10).', source: 'combat.cc:4844' }],
  [64, { tldr: 'Scope — Perception counts ×5 for range, but the scope is useless under 8 hexes.', source: 'combat.cc:4528' }],
  [65, { tldr: 'Fast Reload — reloading costs 1 AP instead of 2.', source: 'item.cc:1730' }],
  [66, { tldr: 'Night Sight — no darkness penalty; night counts as full daylight for this weapon.', source: 'combat.cc:4637' }],
  [67, { tldr: 'Flameboy — kills go up in the flame death animation.', source: 'actions.cc:217' }],
  [117, { tldr: 'Enhanced Knockout — every hit has a (Strength − 8)% chance to knock the target out cold.', source: 'combat.cc:3976' }],
]);

/**
 * The while-worn bonuses armor with this perk grants, as human strings —
 * empty when equipping it changes nothing (rank-checked perks).
 */
export function wornPerkBonuses(perkId: number): string[] {
  const row = PERK_ROWS[perkId];
  if (!row) return [];
  const [, maxRank, , stat, statModifier, , , , , , ...special] = row;
  const out: string[] = [];
  const signed = (n: number) => (n > 0 ? `+${n}` : `${n}`);
  if (stat !== -1 && statModifier !== 0) out.push(`${signed(statModifier)} ${STAT_LABELS[stat] ?? `stat ${stat}`}`);
  if (maxRank === -1) {
    special.forEach((bonus, i) => {
      if (bonus !== 0) out.push(`${signed(bonus)} ${STAT_LABELS[i]}`);
    });
  }
  return out;
}

/** Every perk id worth offering on armor: it must *do* something when worn. */
export function wornPerkChoices(): number[] {
  const out: number[] = [];
  for (let id = 0; id < PERK_ROWS.length; id++) {
    if (wornPerkBonuses(id).length) out.push(id);
  }
  return out;
}
